import Big from 'big.js';

import { type Item } from 'types/item';
import { type TaxMode, type TaxRate } from 'types/tax-rate';
import { type GetInvoiceEmailContentParams, type InvoiceSectionItem, type SendInvoicePdfEmailParams } from 'types/invoice';
import { locationBillingTypes } from 'constants-ts/payments';
import { systemDefaultValues, STRIPE_PROCESSING_FEE_PERCENTAGE, STRIPE_FIXED_FEE, AU_GST_RATE, TAX_MODES } from '@a1/shared';
import callAzureFunction from './call-azure-function';
import handleError from './handle-error';
import callAzureFunctionPublic from './call-azure-function-public';
import type { ButtonProps } from '@mui/material/Button';

// project components
import DescriptionIcon from '@mui/icons-material/Description';
import AwaitingPaymentIcon from '@mui/icons-material/Payment';
import PaidIcon from '@mui/icons-material/CheckCircle';
import VoidIcon from '@mui/icons-material/Cancel';
import RefundedIcon from '@mui/icons-material/Replay';
import PendingIcon from '@mui/icons-material/Autorenew';

const InvoiceIcons = {
  DRAFT: DescriptionIcon,
  AWAITING_PAYMENT: AwaitingPaymentIcon,
  PAID: PaidIcon,
  VOID: VoidIcon,
  REFUNDED: RefundedIcon,
  PENDING: PendingIcon
};

const RE_POSITIVE_DECIMAL_2 = /^(?!.*\..*\.)[0-9]*(\.[0-9]{0,2})?$/;

/**
 * Checks if the given value is a valid tax rate.
 * @returns `true` or `false`
 */
const isValidTaxRate = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

/**
 * Checks if the given value is a valid sales price for an item.
 * @returns `true` or `false`
 */
const isValidSalesPrice = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

/**
 * Checks if the given value is a valid unit amount for an item.
 * @returns `true` or `false`
 */
const isValidUnitAmount = (value: string) => RE_POSITIVE_DECIMAL_2.test(value);

const taxModes: Record<string, TaxMode> = {
  EXCLUSIVE: {
    id: 'EXCLUSIVE',
    label: 'Tax exclusive'
  },
  INCLUSIVE: {
    id: 'INCLUSIVE',
    label: 'Tax inclusive'
  },
  NO_TAX: {
    id: 'NO_TAX',
    label: 'No tax'
  }
};

interface TaxTotal {
  total: number;
  taxRate?: TaxRate;
}

const getTaxAmount = ({ price, taxRate, taxMode }: { price?: Big.BigSource | null; taxRate?: TaxRate | null; taxMode?: string | null }) => {
  const basePrice = new Big(price ?? 0);

  if (!taxRate?.total || !taxMode || taxMode === taxModes.NO_TAX.id) {
    return new Big(0);
  }

  const taxRateBig = new Big(taxRate.total).div(100);

  if (taxMode === taxModes.INCLUSIVE.id) {
    // For inclusive tax: tax = price - (price / (1 + taxRate))
    return basePrice.minus(basePrice.div(new Big(1).plus(taxRateBig))).round(2);
  }
  // For exclusive tax: tax = price * taxRate
  return basePrice.times(taxRateBig).round(2);
};

const getPriceBeforeTax = ({
  price,
  taxRate,
  taxMode
}: {
  price?: Big.BigSource | null;
  taxRate?: TaxRate | null;
  taxMode?: string | null;
}) => {
  const basePrice = new Big(price ?? 0);

  if (!taxRate?.total || !taxMode || taxMode !== taxModes.INCLUSIVE.id) {
    // For exclusive or no tax, price before tax is the same as price
    return basePrice;
  }

  // For inclusive tax: priceBeforeTax = price / (1 + taxRate)
  const taxRateBig = new Big(taxRate.total).div(100);
  return basePrice.div(new Big(1).plus(taxRateBig)).round(2);
};

const getSalesItemTaxAmount = (item: Item | null, billingType: string) => {
  const taxRate = item?.SalesTaxRate;
  const taxMode = item?.salesTaxMode;
  const price =
    billingType === locationBillingTypes.Regional.value && item?.hasRegionalVariant ? item.regVariantUnitAmount : item?.salesPrice;
  return getTaxAmount({ price, taxRate, taxMode });
};

const getSalesItemPriceBeforeTax = (item: Item | null, billingType: string) => {
  const price =
    billingType === locationBillingTypes.Regional.value && item?.hasRegionalVariant ? item.regVariantUnitAmount : item?.salesPrice;
  const taxRate = item?.SalesTaxRate;
  const taxMode = item?.salesTaxMode;
  return getPriceBeforeTax({ price, taxRate, taxMode });
};

/**
 * Calculates the markup value needed to cancel out the provided fee percentage and fixed fee.
 * This function uses Big.js for arbitrary-precision arithmetic to avoid floating-point errors.
 *
 * @param {number} amount - The desired amount that the recipient should receive.
 * @param {number} feePercentage - The percentage fee (as a decimal, e.g., 0.017 for 1.7%).
 * @param {number} feeFixed - The fixed fee amount (as a decimal, e.g., 0.30 for 30 cents).
 * @param {number} decimalPlaces - The rounding number.
 * @returns {Big} A Big.js object representing the markup to be added to the amount,
 * rounded to specified decimal places. This value should be converted to a number
 * using `.toNumber()` if a primitive number is required for API calls.
 */
const getMarkupValue = (amount: number | Big = 0, feePercentage = 0, feeFixed = 0, gstRate = 0, decimalPlaces = 2) => {
  const amountBig = new Big(amount);

  const STRIPE_PERCENTAGE_GROSS = new Big(feePercentage);
  const STRIPE_FIXED_GROSS = new Big(feeFixed);
  const GST_RATE = new Big(gstRate);

  // --- Step 1: Calculate the *estimated* total amount to charge the customer (C) ---
  // Formula: C = (N + F_gross) / (1 - P_gross)
  // This 'C' is the target charge amount.
  const numeratorForCharge = amountBig.plus(STRIPE_FIXED_GROSS);
  const denominatorForCharge = new Big(1).minus(STRIPE_PERCENTAGE_GROSS);
  const estimatedChargeAmountRaw = numeratorForCharge.div(denominatorForCharge);

  // Round first before calculating the fees, because stripe charged on the actual amount.
  const estimatedChargeAmount = estimatedChargeAmountRaw.round(decimalPlaces, Big.roundHalfUp);

  // Split percentage and fixed fee into pre-tax components
  const divisorForPreTax = new Big(1).plus(GST_RATE);
  const preTaxPercentageRate = STRIPE_PERCENTAGE_GROSS.div(divisorForPreTax);
  const preTaxFixedFee = STRIPE_FIXED_GROSS.div(divisorForPreTax);

  // Calculate the actual fee without gst
  const rawPreTaxFee = estimatedChargeAmount.times(preTaxPercentageRate).plus(preTaxFixedFee);
  const roundedPreTaxFee = rawPreTaxFee.round(decimalPlaces, Big.roundHalfUp);

  // Calculate the GST
  const rawTaxAmount = roundedPreTaxFee.times(GST_RATE);
  const roundedTaxAmount = rawTaxAmount.round(decimalPlaces, Big.roundHalfUp);

  // Add them together
  const totalExpectedStripeFee = roundedPreTaxFee.plus(roundedTaxAmount);

  return totalExpectedStripeFee;
};

const getSalesItemTotal = (items: (Item | null)[], billingType: string, includeFees = false) => {
  const salesItems: Item[] = items.filter((item): item is Item => item !== null && item.isSalesItem);

  // item subtotal
  let subtotal = new Big(0);
  // total per tax rate
  const taxTotals: Record<string, TaxTotal> = {};
  // total per fee
  const feeTotals: Record<string, number> = {};
  // total after taxes and fees
  let total = new Big(0);

  salesItems.forEach((item) => {
    const { SalesTaxRate, salesTaxMode } = item;

    // Get pre-tax price and tax amount using corrected functions
    const priceBeforeTax = getSalesItemPriceBeforeTax(item, billingType);
    const taxAmount = getSalesItemTaxAmount(item, billingType);

    // Add pre-tax price to subtotal
    subtotal = subtotal.plus(priceBeforeTax);

    // Save total per tax rate if tax applies
    if (SalesTaxRate && salesTaxMode && salesTaxMode !== taxModes.NO_TAX.id) {
      if (taxTotals[SalesTaxRate.id]) {
        taxTotals[SalesTaxRate.id].total += taxAmount.round(2).toNumber();
        taxTotals[SalesTaxRate.id].taxRate = SalesTaxRate;
      } else {
        taxTotals[SalesTaxRate.id] = {
          total: taxAmount.round(2).toNumber(),
          taxRate: SalesTaxRate
        };
      }
    }
  });

  // add the subtotal
  total = total.plus(subtotal);
  // add tax rate totals
  Object.values(taxTotals).forEach((taxTotal) => {
    total = total.plus(new Big(taxTotal.total));
  });

  if (includeFees && total.toNumber() > 0) {
    // markup for Stripe fees using domestic card pricing (AU)
    // https://stripe.com/au/pricing/local-payment-methods
    feeTotals['Processing Fee'] = getMarkupValue(total, STRIPE_PROCESSING_FEE_PERCENTAGE, STRIPE_FIXED_FEE, AU_GST_RATE).toNumber();

    // add fees
    Object.values(feeTotals).forEach((feeTotal) => {
      total = total.plus(new Big(feeTotal));
    });
  }

  return { subtotal: subtotal.toNumber(), taxTotals, feeTotals, total: total.toNumber() };
};

const getInvoiceSectionItemsTotal = (invoiceSectionItems: InvoiceSectionItem[]) => {
  // item subtotal
  let subtotal = new Big(0);
  // total per tax rate
  const taxTotals: Record<string, TaxTotal> = {};
  // total after tax
  let total = new Big(0);

  /**
   * Prices in this section were already calculated as base prices in the backend.
   */
  invoiceSectionItems.forEach((item) => {
    const { quantity, price, taxRate, taxMode, taxName } = item;
    const totalPrice = new Big(price).times(quantity);

    if (taxRate && taxMode !== TAX_MODES.NO_TAX.id) {
      let taxAmount = new Big(0);

      taxAmount = totalPrice.times(new Big(taxRate).div(100)).round(2);

      subtotal = subtotal.plus(totalPrice);

      const taxKey = taxName || 'Tax';

      // save total per tax rate
      if (taxTotals[taxKey]) {
        taxTotals[taxKey].total += taxAmount.toNumber();
      } else {
        taxTotals[taxKey] = {
          total: taxAmount.toNumber()
        };
      }
    } else {
      // no tax is applied
      subtotal = subtotal.plus(totalPrice);
    }
  });

  // finalize total by adding the subtotal of items and all the totals per tax rate
  total = total.plus(subtotal);
  Object.values(taxTotals).forEach((taxTotal) => {
    total = total.plus(new Big(taxTotal.total));
  });

  return { subtotal: subtotal.toNumber(), taxTotals, total: total.toNumber() };
};

// const getCurrencySymbol = (currency: string) => {
//   const upperCode = currency.toUpperCase();
//   const formatter = new Intl.NumberFormat(systemDefaultValues.locale, {
//     style: 'currency',
//     currency: upperCode,
//     currencyDisplay: 'symbol',
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0
//   });
//
//   const formatted = formatter.format(1);
//   return formatted.replace(/[0-9\s,.]/g, '');
// };

const getCurrencyName = (currency: string) => {
  const displayNames = new Intl.DisplayNames([systemDefaultValues.locale], { type: 'currency' });
  return displayNames.of(currency.toUpperCase());
};

const formatWithCurrency = (value: number | Big, currency: string) => {
  const upperCode = currency.toUpperCase();
  value = new Big(value ?? 0);
  /**
   * Using Code for now, maybe consider symbol later in the future
   *
   * const formatter = new Intl.NumberFormat(systemDefaultValues.locale, {
   *   style: 'currency',
   *   currency: upperCode,
   *   minimumFractionDigits: 2,
   *   maximumFractionDigits: 2
   * });
   */

  const numericValue = value.toNumber();
  // return formatter.format(numericValue);
  return `${upperCode} ${numericValue.toFixed(2)}`;
};

const sendInvoicePdfEmail = async ({
  emails,
  subject,
  html,
  pdfBlob,
  invoiceNumber,
  invoiceId,
  invoiceToken,
  onSuccess,
  onError
}: SendInvoicePdfEmailParams) => {
  try {
    const formData = new FormData();
    formData.append('emails', emails.join(','));
    formData.append('subject', subject);
    formData.append('html', html);
    formData.append('pdfFile', pdfBlob, `${invoiceNumber ? invoiceNumber : `INV-${invoiceId}`}.pdf`);
    formData.append('invoiceId', invoiceId ?? '');

    let data: string;

    if (invoiceToken) {
      const response = await callAzureFunctionPublic<string>({
        url: 'public_invoices/send-pdf',
        method: 'post',
        data: formData,
        headers: {
          Authorization: `Bearer ${invoiceToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      data = response.data;
    } else {
      const response = await callAzureFunction<string>({
        url: 'invoices/send-pdf',
        method: 'post',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      data = response.data;
    }

    onSuccess?.(data);
    return data;
  } catch (error) {
    const message = handleError(error);
    onError?.(message);
    throw error;
  }
};

const getInvoiceEmailContent = ({
  firstName,
  fullName,
  bookingId,
  invoiceId,
  bookingEntityName,
  isAddOnCharge
}: GetInvoiceEmailContentParams) => {
  if (isAddOnCharge) {
    const subject = `Additional Charge for ${bookingEntityName} #[${bookingId}] - ${fullName}`;
    const emailBody = `
      <p>Dear ${firstName},</p>
      <p>A new charge has been added to your booking. Please find the invoice INV-${invoiceId} for this charge attached.</p>
    `;
    return { subject, emailBody };
  }

  const subject = `Invoice for ${bookingEntityName} #[${bookingId}] - ${fullName}`;
  const emailBody = `
    <p>Dear ${firstName},</p>
    <p>Thank you for your booking. Please find the invoice INV-${invoiceId} attached.</p>
  `;

  return { subject, emailBody };
};

const getOnlineButtonProps = (
  willPayOnline: boolean
): {
  variant: ButtonProps['variant'];
  color: ButtonProps['color'];
} => ({
  variant: willPayOnline ? 'contained' : 'outlined',
  color: willPayOnline ? 'secondary' : 'primary'
});

const getInvoiceOnlyButtonProps = (
  willPayOnline: boolean
): {
  variant: ButtonProps['variant'];
  color: ButtonProps['color'];
} => ({
  variant: !willPayOnline ? 'contained' : 'outlined',
  color: !willPayOnline ? 'secondary' : 'primary'
});

export {
  isValidTaxRate,
  isValidSalesPrice,
  isValidUnitAmount,
  taxModes,
  getSalesItemTaxAmount,
  getSalesItemPriceBeforeTax,
  getSalesItemTotal,
  getInvoiceSectionItemsTotal,
  // getCurrencySymbol,
  getCurrencyName,
  formatWithCurrency,
  sendInvoicePdfEmail,
  getInvoiceEmailContent,
  getOnlineButtonProps,
  getInvoiceOnlyButtonProps,
  InvoiceIcons
};
